import Order from 'communication/types/order/Order';
import { arraysDiffer, objectArraysDiffer } from 'utils/arrays/arrayUtils';
import {
    buildPortfolioSelection,
    emptySelectionState,
    PortfolioPosition,
    PortfolioSelection,
    PortfolioSymbolQuantity,
} from 'portfolio/PortfolioSelection';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';

const toPortfolioPositionId = (p?: PortfolioPosition) =>
    `${p?.symbol || ''}-${p?.side || ''}`;

/*
 * Wraps a Portfolio Selection adding some additional accessors
 */
export class PortfolioSelectionWrapper implements PortfolioSelection {
    private _state: PortfolioSelection;

    public static buildWithInstinetSymbology(
        portfolioSelectionState?: PortfolioSelection
    ) {
        if (!portfolioSelectionState) {
            return new PortfolioSelectionWrapper(emptySelectionState);
        }

        if (portfolioSelectionState.symbology === SymbologyValues.Instinet) {
            return new PortfolioSelectionWrapper(portfolioSelectionState);
        }

        if (portfolioSelectionState.selectedOrders.length > 0) {
            // With orders, we can rebuild using the Instinet symbology....
            const instinetSelection = buildPortfolioSelection({
                selectedOrders: portfolioSelectionState.selectedOrders,
                symbology: SymbologyValues.Instinet,
            });
            return new PortfolioSelectionWrapper(instinetSelection);
        }
        return new PortfolioSelectionWrapper(emptySelectionState);
    }

    public constructor(state?: PortfolioSelection) {
        this._state = state || emptySelectionState;
    }

    public get symbology(): SymbologyValues {
        return this._state.symbology;
    }

    public get selectedOrders(): Order[] {
        return this._state.selectedOrders;
    }

    public get selectedPositions(): PortfolioPosition[] {
        return this._state.selectedPositions;
    }

    public get selectedSymbolQuantities(): PortfolioSymbolQuantity[] {
        return this._state.selectedSymbolQuantities;
    }

    public get selectedSymbols(): string[] {
        return this._state.selectedSymbols;
    }

    public get selectedOrder(): Order | undefined {
        return this.selectedOrders.length === 1
            ? this.selectedOrders[0]
            : undefined;
    }

    public get selectedPosition(): PortfolioPosition | undefined {
        return this.selectedPositions.length === 1
            ? this.selectedPositions[0]
            : undefined;
    }

    public get selectedSymbolQuantity(): PortfolioSymbolQuantity | undefined {
        return this.selectedSymbolQuantities.length === 1
            ? this.selectedSymbolQuantities[0]
            : undefined;
    }

    public get selectedSymbol(): string | undefined {
        return this.selectedSymbols.length === 1
            ? this.selectedSymbols[0]
            : undefined;
    }

    public orderSelectionDiffersFrom(
        other: PortfolioSelectionWrapper
    ): boolean {
        return objectArraysDiffer(
            this.selectedOrders,
            other.selectedOrders,
            'orderID'
        );
    }

    /*
     * The position selection has changed.
     * Includes calculated values in the diff, so could be due to only the qty etc changing.
     * Consuming widgets may need to update their streams
     */
    public positionSelectionDiffersFrom(
        other: PortfolioSelectionWrapper
    ): boolean {
        return (
            this.symbology !== other.symbology ||
            objectArraysDiffer(
                this.selectedPositions,
                other.selectedPositions,
                (p) => `${p.symbol}-${p.side}-${p.qty}-${p.qtyRemaining}`
            )
        );
    }

    /*
     * The position selection identity has changed.
     * Does not includes calculated values in the diff, so only true when selected symbol or side changes
     * Consuming widgets will need to clear their current data before updating their streams
     */
    public positionSelectionDiffersByIdentityFrom(
        other: PortfolioSelectionWrapper
    ): boolean {
        return objectArraysDiffer(
            this.selectedPositions,
            other.selectedPositions,
            toPortfolioPositionId
        );
    }

    /*
     * The symbol quantity selection has changed.
     * Includes calculated values in the diff, so could be due to only the qty etc changing.
     * Consuming widgets may need to update their streams
     */
    public symbolQuantitySelectionDiffersFrom(
        other: PortfolioSelectionWrapper
    ): boolean {
        return objectArraysDiffer(
            this.selectedSymbolQuantities,
            other.selectedSymbolQuantities,
            (p) => `${p.symbol}-${p.qty}-${p.qtyRemaining}`
        );
    }

    /*
     * The symbol quantity identity has changed.
     * Does not includes calculated values in the diff, so only true when selected symbol changes
     * Consuming widgets will need to clear their current data before updating their streams
     */
    public symbolQuantitySelectionDiffersByIdentityFrom(
        other: PortfolioSelectionWrapper
    ): boolean {
        return objectArraysDiffer(
            this.selectedSymbolQuantities,
            other.selectedSymbolQuantities,
            (p) => `${p.symbol}`
        );
    }

    public symbolSelectionDiffersFrom(
        other: PortfolioSelectionWrapper
    ): boolean {
        return arraysDiffer(this.selectedSymbols, other.selectedSymbols);
    }

    public selectionDiffersFrom(other: PortfolioSelectionWrapper): boolean {
        return (
            this.orderSelectionDiffersFrom(other) ||
            this.positionSelectionDiffersFrom(other) ||
            this.symbolQuantitySelectionDiffersFrom(other) ||
            this.symbolSelectionDiffersFrom(other)
        );
    }
}
